import React, { useState, useEffect } from 'react';
import { Button, Badge } from 'react-bootstrap';
import './MultiFileUpload.scss';

const MultiFileUpload = ({
  name,
  id,
  maxFiles = 3,
  maxFileSize = 10 * 1024 * 1024, // 10MB
  acceptedTypes = ['.jpg', '.jpeg', '.png', '.pdf'],
  existingFiles = [],
  existingURLs = [],
  onFilesChange,
  onDeleteFile,
  disabled = false,
  error,
  touched
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [fileErrors, setFileErrors] = useState([]);

  // Reset file input when existingFiles change (after successful upload)
  useEffect(() => {
    const fileInput = document.getElementById(id);
    if (fileInput) {
      fileInput.value = '';
    }
    setSelectedFiles([]);
    setFileErrors([]);
  }, [existingFiles.length, id]);

  const validateFile = (file) => {
    const errors = [];

    // Check file size
    if (file.size > maxFileSize) {
      errors.push(`File size exceeds ${maxFileSize / (1024 * 1024)}MB`);
    }

    // Check file type
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (!acceptedTypes.includes(fileExt)) {
      errors.push(`File type ${fileExt} not accepted`);
    }

    return errors;
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
  };

  const processFiles = (files) => {
    const totalFiles = selectedFiles.length + existingFiles.length + files.length;

    if (totalFiles > maxFiles) {
      setFileErrors([`Maximum ${maxFiles} files allowed`]);
      return;
    }

    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      const fileErrors = validateFile(file);
      if (fileErrors.length === 0) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${fileErrors.join(', ')}`);
      }
    });

    setFileErrors(errors);
    const newSelectedFiles = [...selectedFiles, ...validFiles];
    setSelectedFiles(newSelectedFiles);

    if (onFilesChange) {
      onFilesChange(newSelectedFiles);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      processFiles(files);
    }
  };

  const removeSelectedFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    if (onFilesChange) {
      onFilesChange(newFiles);
    }
  };

  const handleDeleteExistingFile = (index) => {
    console.log('MultiFileUpload - handleDeleteExistingFile called with index:', index);
    console.log('MultiFileUpload - existingFiles length:', existingFiles.length);
    console.log('MultiFileUpload - existingFiles:', existingFiles);
    if (onDeleteFile) {
      onDeleteFile(index);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (ext === 'pdf') return '📄';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return '🖼️';
    return '📎';
  };

  return (
    <div className="multi-file-upload">
      {/* Upload Button - Matching FileUpload Design */}
      <div className='form-control' style={{ display: 'flex', flexDirection: 'row', gap: 10, alignItems: 'center', paddingLeft: '2px' }}>
        <input
          type="file"
          id={id}
          name={name}
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleFileSelect}
          disabled={disabled}
          style={{ display: 'none' }}
        />
        <label
          htmlFor={id}
          className='mt-2'
          style={{
            display: "inline-block",
            padding: "4px",
            backgroundColor: disabled ? "#6eaaf4" : "#007BFF",
            color: "#FFFFFF",
            borderRadius: "4px",
            cursor: disabled ? 'auto' : 'pointer',
            fontSize: "14px"
          }}
        >
          {selectedFiles.length > 0 ? 'Add More Files' : 'Upload File'}
        </label>

        <p
          className="mt-3"
          style={{
            flex: 1,
            whiteSpace: "nowrap",
            overflow: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {selectedFiles.length > 0
            ? `${selectedFiles.length} file(s) selected`
            : `Max ${maxFileSize / (1024 * 1024)}MB, ${acceptedTypes.join(', ').toUpperCase()}`
          }
        </p>
      </div>

      {/* Error Messages */}
      {fileErrors.length > 0 && (
        <div className="file-errors">
          {fileErrors.map((error, index) => (
            <small key={index} className="text-danger d-block">{error}</small>
          ))}
        </div>
      )}

      {error && touched && (
        <small className="text-danger d-block mt-2">{error}</small>
      )}

      {/* Selected Files (Not yet uploaded) */}
      {selectedFiles.length > 0 && (
        <div className="selected-files mt-3">
          <strong>Selected Files ({selectedFiles.length}):</strong>
          {selectedFiles.map((file, index) => (
            <div key={index} className="file-item">
              <span className="file-icon">{getFileIcon(file.name)}</span>
              <span className="file-name">{file.name}</span>
              <span className="file-size">({formatFileSize(file.size)})</span>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() => removeSelectedFile(index)}
                disabled={disabled}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Existing Uploaded Files */}
      {existingFiles.length > 0 && (
        <div className="existing-files mt-3">
          <strong>Currently Uploaded ({existingFiles.length}):</strong>
          {existingFiles.map((file, index) => {
            const fileName = file.split('/').pop();
            return (
              <div key={index} className="file-item uploaded">
                <span className="file-icon">{getFileIcon(fileName)}</span>
                <span className="file-name">{fileName}</span>
                {existingURLs[index] && (
                  <a
                    href={existingURLs[index]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary"
                  >
                    View
                  </a>
                )}
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => handleDeleteExistingFile(index)}
                  disabled={disabled}
                >
                  Delete
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary */}
      {(existingFiles.length > 0 || selectedFiles.length > 0) && (
        <div className="upload-summary mt-2">
          <Badge bg="info">
            Total: {existingFiles.length + selectedFiles.length} / {maxFiles} files
          </Badge>
        </div>
      )}
    </div>
  );
};

export default MultiFileUpload;
